import * as React from 'react';

import {
  Area as _Area,
  AreaChart,
  type AreaProps,
  Bar as _Bar,
  BarChart,
  type BarProps,
  CartesianGrid,
  Cell,
  Legend as _Legend,
  type LegendProps,
  Line as _Line,
  LineChart,
  type LineProps,
  Pie as _Pie,
  PieChart,
  type PieProps,
  ResponsiveContainer,
  Scatter as _Scatter,
  ScatterChart,
  type ScatterProps,
  Tooltip as _Tooltip,
  type TooltipProps,
  XAxis as _XAxis,
  type XAxisProps,
  YAxis as _YAxis,
  type YAxisProps,
} from 'recharts';

import { cn } from '../../lib/stylingUtils';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type FC<P = {}> = React.ComponentType<P>;
const XAxis = _XAxis as unknown as FC<XAxisProps>;
const YAxis = _YAxis as unknown as FC<YAxisProps>;
const Tooltip = _Tooltip as unknown as FC<TooltipProps<string, string>>;
const Legend = _Legend as unknown as FC<LegendProps>;
const Line = _Line as unknown as FC<LineProps>;
const Area = _Area as unknown as FC<AreaProps>;
const Bar = _Bar as unknown as FC<BarProps>;
const Pie = _Pie as unknown as FC<PieProps>;
const Scatter = _Scatter as unknown as FC<ScatterProps>;

export interface ChartProps {
  /**
   * Chart type
   */
  type:
    | 'line'
    | 'area'
    | 'bar'
    | 'column'
    | 'pie'
    | 'donut'
    | 'scatter'
    | 'bubble';
  /**
   * Chart title
   */
  title?: string;
  /**
   * Data array (or JSON string when used via markdown)
   */
  data: Array<Record<string, unknown>> | string;
  /**
   * Key for X-axis data
   */
  xKey?: string;
  /**
   * Key(s) for Y-axis data (comma-separated string or array)
   */
  yKey?: string;
  /**
   * Additional chart configuration (or JSON string when used via markdown)
   */
  config?: Record<string, unknown> | string;
  /**
   * Optional className for styling
   */
  className?: string;
}

const COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#14b8a6',
  '#f97316',
];

/**
 * Chart component that renders interactive data visualizations using Recharts
 *
 * Supports multiple chart types: line, area, bar, column, pie, donut, scatter, bubble
 *
 * @example
 * ```tsx
 * <Chart
 *   type="line"
 *   title="Revenue Over Time"
 *   data={[
 *     { month: 'Jan', revenue: 1000 },
 *     { month: 'Feb', revenue: 1500 }
 *   ]}
 *   xKey="month"
 *   yKey="revenue"
 * />
 * ```
 */
export const Chart: React.FC<ChartProps> = ({
  type,
  title,
  data: dataProp,
  xKey,
  yKey,
  config: configProp,
  className,
}) => {
  // Parse data if it's a string (from react-markdown serialization)
  const data = React.useMemo(() => {
    if (typeof dataProp === 'string') {
      try {
        const parsed = JSON.parse(dataProp);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        console.error('Failed to parse chart data:', dataProp);
        return [];
      }
    }
    return Array.isArray(dataProp) ? dataProp : [];
  }, [dataProp]);

  // Parse config if it's a string (from react-markdown serialization)
  // TODO implement configuration
  const _config = React.useMemo(() => {
    if (typeof configProp === 'string') {
      try {
        return JSON.parse(configProp) as Record<string, unknown>;
      } catch {
        console.error('Failed to parse chart config:', configProp);
        return undefined;
      }
    }
    return configProp;
  }, [configProp]);

  const yKeys = React.useMemo(() => {
    if (!yKey) return [];
    return yKey.includes(',') ? yKey.split(',').map((k) => k.trim()) : [yKey];
  }, [yKey]);

  if (!data || data.length === 0) {
    return (
      <span
        className={className}
        style={{
          display: 'inline-block',
          width: '100%',
          padding: '2rem',
          textAlign: 'center',
          color: 'rgba(0, 0, 0, 0.5)',
          border: '1px dashed rgba(0, 0, 0, 0.2)',
          borderRadius: '0.5rem',
          margin: '1rem 0',
        }}
      >
        No data available for chart
      </span>
    );
  }

  const ChartWrapper: React.FC<{ children: React.ReactElement }> = ({
    children,
  }) => (
    <span
      className={cn('@ChartWrapper', className)}
      style={{ display: 'inline-block', width: '100%', margin: '1.5rem 0' }}
    >
      {title && (
        <span
          style={{
            display: 'block',
            marginBottom: '1rem',
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'rgba(0, 0, 0, 0.9)',
          }}
        >
          {title}
        </span>
      )}
      <ResponsiveContainer width="100%" height={400}>
        {children}
      </ResponsiveContainer>
    </span>
  );

  switch (type) {
    case 'line':
      return (
        <ChartWrapper>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 0, 0, 0.1)" />
            {xKey && <XAxis dataKey={xKey} />}
            <YAxis />
            <Tooltip />
            <Legend />
            {yKeys.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={COLORS[index % COLORS.length]}
                strokeWidth={2}
                dot={{ fill: COLORS[index % COLORS.length] }}
              />
            ))}
          </LineChart>
        </ChartWrapper>
      );

    case 'area':
      return (
        <ChartWrapper>
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 0, 0, 0.1)" />
            {xKey && <XAxis dataKey={xKey} />}
            <YAxis />
            <Tooltip />
            <Legend />
            {yKeys.map((key, index) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={COLORS[index % COLORS.length]}
                fill={COLORS[index % COLORS.length]}
                fillOpacity={0.6}
              />
            ))}
          </AreaChart>
        </ChartWrapper>
      );

    case 'bar':
    case 'column':
      return (
        <ChartWrapper>
          <BarChart
            data={data}
            layout={type === 'bar' ? 'vertical' : 'horizontal'}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 0, 0, 0.1)" />
            {type === 'bar' ? (
              <>
                <XAxis type="number" />
                <YAxis dataKey={xKey} type="category" />
              </>
            ) : (
              <>
                {xKey && <XAxis dataKey={xKey} />}
                <YAxis />
              </>
            )}
            <Tooltip />
            <Legend />
            {yKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </BarChart>
        </ChartWrapper>
      );

    case 'pie':
    case 'donut': {
      const valueKey = yKeys[0] || 'value';
      const nameKey = xKey || 'name';

      return (
        <ChartWrapper>
          <PieChart>
            <Pie
              data={data}
              dataKey={valueKey}
              nameKey={nameKey}
              cx="50%"
              cy="50%"
              innerRadius={type === 'donut' ? '40%' : 0}
              outerRadius="70%"
              label
            >
              {data.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ChartWrapper>
      );
    }

    case 'scatter':
    case 'bubble':
      return (
        <ChartWrapper>
          <ScatterChart>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 0, 0, 0.1)" />
            {xKey && <XAxis dataKey={xKey} name={xKey} />}
            {yKeys[0] && <YAxis dataKey={yKeys[0]} name={yKeys[0]} />}
            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
            <Legend />
            {yKeys.map((key, index) => (
              <Scatter
                key={key}
                name={key}
                data={data}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </ScatterChart>
        </ChartWrapper>
      );

    default:
      return (
        <span
          className={className}
          style={{
            display: 'inline-block',
            width: '100%',
            padding: '2rem',
            textAlign: 'center',
            color: 'rgba(255, 0, 0, 0.7)',
            border: '1px solid rgba(255, 0, 0, 0.3)',
            borderRadius: '0.5rem',
            margin: '1rem 0',
          }}
        >
          Unsupported chart type: {type}
        </span>
      );
  }
};

Chart.displayName = 'Chart';
